import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  DollarSign, TrendingUp, TrendingDown, BarChart3, PieChart,
  ArrowLeft, Calendar, Filter, Download, RefreshCw, Building2,
  FileText, Users, AlertCircle, CheckCircle2, Clock, ChevronRight,
  ArrowUpRight, ArrowDownRight, Minus, MoreHorizontal, Eye,
  Receipt, CreditCard, Wallet, Target, Activity, Layers
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import BlueprintIndicator from '@/components/Blueprint/BlueprintIndicator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';
import CashFlowWidget from '@/components/widgets/CashFlowWidget';
import ProfitByProjectWidget from '@/components/widgets/ProfitByProjectWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { useRole } from '@/contexts/RoleContext';

// Format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Format percentage
const formatPercent = (value) => {
  if (value === null || value === undefined) return '0%';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

// Status colors and labels
const PROJECT_STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-500', textColor: 'text-emerald-600' },
  on_hold: { label: 'On Hold', color: 'bg-amber-500', textColor: 'text-amber-600' },
  completed: { label: 'Completed', color: 'bg-blue-500', textColor: 'text-blue-600' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-500', textColor: 'text-gray-600' },
};

// Metric Card Component
function MetricCard({ title, value, subtitle, icon: Icon, trend, trendLabel, color = 'text-[#70170F]', helpId }) {
  const trendColor = trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-red-600' : 'text-gray-500';
  const TrendIcon = trend > 0 ? ArrowUpRight : trend < 0 ? ArrowDownRight : Minus;
  
  return (
    <Card className="relative overflow-hidden">
      {helpId && <HelpIcon elementId={helpId} position="top-right" />}
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground font-medium">{title}</p>
            <p className={`text-2xl font-light tracking-tight ${color}`}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className={`p-2 rounded-lg bg-[#E6DCD1]/50`}>
            <Icon className={`w-5 h-5 ${color}`} strokeWidth={1.5} />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-3 text-xs ${trendColor}`}>
            <TrendIcon className="w-3 h-3" />
            <span>{formatPercent(trend)}</span>
            {trendLabel && <span className="text-muted-foreground ml-1">{trendLabel}</span>}
          </div>
        )}
      </CardContent>
      {/* Decorative gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#70170F]/20 via-[#BC5D41]/20 to-transparent" />
    </Card>
  );
}

// Project Financial Row
function ProjectFinancialRow({ project, onClick }) {
  const budgetUsed = project.actual_cost && project.estimated_budget 
    ? (project.actual_cost / project.estimated_budget) * 100 
    : 0;
  const variance = project.estimated_budget - (project.actual_cost || 0);
  const isOverBudget = variance < 0;
  
  return (
    <div 
      className="flex items-center gap-4 p-4 hover:bg-muted/50 cursor-pointer border-b border-border last:border-0 transition-colors"
      onClick={onClick}
      data-testid={`project-row-${project.id}`}
    >
      {/* Project Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{project.name}</span>
          {project.project_code && (
            <Badge variant="outline" className="text-xs">{project.project_code}</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate mt-0.5">
          {project.client_name || 'No client'} • {project.project_type || 'Project'}
        </p>
      </div>
      
      {/* Budget Progress */}
      <div className="w-32 hidden md:block">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-muted-foreground">Budget</span>
          <span className={isOverBudget ? 'text-red-600' : 'text-muted-foreground'}>
            {budgetUsed.toFixed(0)}%
          </span>
        </div>
        <Progress 
          value={Math.min(budgetUsed, 100)} 
          className={`h-1.5 ${isOverBudget ? '[&>div]:bg-red-500' : ''}`}
        />
      </div>
      
      {/* Estimated */}
      <div className="text-right w-24 hidden sm:block">
        <p className="text-xs text-muted-foreground">Estimated</p>
        <p className="font-medium text-sm">{formatCurrency(project.estimated_budget || 0)}</p>
      </div>
      
      {/* Actual */}
      <div className="text-right w-24">
        <p className="text-xs text-muted-foreground">Actual</p>
        <p className="font-medium text-sm">{formatCurrency(project.actual_cost || 0)}</p>
      </div>
      
      {/* Variance */}
      <div className="text-right w-24 hidden lg:block">
        <p className="text-xs text-muted-foreground">Variance</p>
        <p className={`font-medium text-sm ${isOverBudget ? 'text-red-600' : 'text-emerald-600'}`}>
          {isOverBudget ? '-' : '+'}{formatCurrency(Math.abs(variance))}
        </p>
      </div>
      
      {/* Status */}
      <div className="w-20">
        <Badge 
          variant="secondary" 
          className={`text-xs ${PROJECT_STATUS_CONFIG[project.status]?.color || 'bg-gray-500'} text-white`}
        >
          {PROJECT_STATUS_CONFIG[project.status]?.label || project.status}
        </Badge>
      </div>
      
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </div>
  );
}

// Recent Transaction Row
function TransactionRow({ transaction }) {
  const isExpense = transaction.type === 'expense';
  
  return (
    <div className="flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors">
      <div className={`p-2 rounded-lg ${isExpense ? 'bg-red-100' : 'bg-emerald-100'}`}>
        {isExpense ? (
          <ArrowDownRight className="w-4 h-4 text-red-600" />
        ) : (
          <ArrowUpRight className="w-4 h-4 text-emerald-600" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{transaction.description}</p>
        <p className="text-xs text-muted-foreground">
          {transaction.project_name || 'Company'} • {transaction.category}
        </p>
      </div>
      <div className="text-right">
        <p className={`font-medium ${isExpense ? 'text-red-600' : 'text-emerald-600'}`}>
          {isExpense ? '-' : '+'}{formatCurrency(transaction.amount)}
        </p>
        <p className="text-xs text-muted-foreground">
          {new Date(transaction.date).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}

// Cash Flow Chart (Simple Bar Visualization)
function CashFlowChart({ data }) {
  const maxValue = Math.max(...data.map(d => Math.max(d.income, d.expenses)));
  
  return (
    <div className="space-y-3">
      {data.map((month, idx) => (
        <div key={idx} className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground w-12">{month.label}</span>
            <div className="flex items-center gap-4">
              <span className="text-emerald-600">{formatCurrency(month.income)}</span>
              <span className="text-red-600">{formatCurrency(month.expenses)}</span>
            </div>
          </div>
          <div className="flex gap-1 h-4">
            <div 
              className="bg-emerald-500 rounded-sm transition-all"
              style={{ width: `${(month.income / maxValue) * 50}%` }}
            />
            <div 
              className="bg-red-400 rounded-sm transition-all"
              style={{ width: `${(month.expenses / maxValue) * 50}%` }}
            />
          </div>
        </div>
      ))}
      <div className="flex items-center gap-4 pt-2 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-emerald-500 rounded-sm" />
          <span className="text-muted-foreground">Income</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-400 rounded-sm" />
          <span className="text-muted-foreground">Expenses</span>
        </div>
      </div>
    </div>
  );
}

// Back of House - Draw Requests Component
function DrawRequestsBackOfHouse() {
  const [draws, setDraws] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchDraws();
  }, []);
  
  const fetchDraws = async () => {
    try {
      const res = await axios.get(`${API}/draw-requests`);
      setDraws(res.data.draw_requests || []);
      setSummary(res.data.summary || {});
    } catch (error) {
      console.error('Error fetching draws:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };
  
  const STATUS_CONFIG = {
    draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
    pending_signature: { label: 'Pending Signature', color: 'bg-amber-100 text-amber-700' },
    signed: { label: 'Signed', color: 'bg-blue-100 text-blue-700' },
    submitted_to_title: { label: 'At Title Co.', color: 'bg-purple-100 text-purple-700' },
    funded: { label: 'Funded', color: 'bg-green-100 text-green-700' },
  };
  
  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading draw requests...</div>;
  }
  
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="p-4 rounded-lg bg-gray-50 border">
          <p className="text-sm text-muted-foreground">Total Requested</p>
          <p className="text-xl font-semibold">{formatCurrency(summary.total_requested)}</p>
        </div>
        <div className="p-4 rounded-lg bg-green-50 border border-green-100">
          <p className="text-sm text-green-700">Total Funded</p>
          <p className="text-xl font-semibold text-green-700">{formatCurrency(summary.total_funded)}</p>
        </div>
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
          <p className="text-sm text-amber-700">Pending Signature</p>
          <p className="text-xl font-semibold text-amber-700">{formatCurrency(summary.pending_signature)}</p>
        </div>
        <div className="p-4 rounded-lg bg-purple-50 border border-purple-100">
          <p className="text-sm text-purple-700">Pending Funding</p>
          <p className="text-xl font-semibold text-purple-700">{formatCurrency(summary.pending_funding)}</p>
        </div>
      </div>
      
      {/* Draw Requests Table */}
      {draws.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>No draw requests yet</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 text-sm font-medium">Draw #</th>
                <th className="text-left p-3 text-sm font-medium">Project</th>
                <th className="text-left p-3 text-sm font-medium">Date</th>
                <th className="text-left p-3 text-sm font-medium">Title Company</th>
                <th className="text-right p-3 text-sm font-medium">Total</th>
                <th className="text-left p-3 text-sm font-medium">Status</th>
                <th className="p-3 text-sm font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {draws.map((draw) => (
                <tr key={draw.id} className="border-t hover:bg-gray-50">
                  <td className="p-3 font-medium">#{draw.draw_number}</td>
                  <td className="p-3">
                    <button 
                      className="text-left hover:text-[#BC5D41]"
                      onClick={() => navigate(`/projects/${draw.project_id}/hub`)}
                    >
                      {draw.project_name}
                    </button>
                  </td>
                  <td className="p-3 text-sm text-muted-foreground">{draw.date_submitted}</td>
                  <td className="p-3 text-sm">{draw.title_company_name || '-'}</td>
                  <td className="p-3 text-right font-semibold">{formatCurrency(draw.total_draw)}</td>
                  <td className="p-3">
                    <Badge className={STATUS_CONFIG[draw.status]?.color}>
                      {STATUS_CONFIG[draw.status]?.label}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate(`/projects/${draw.project_id}/draw-requests`)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function FinancialsPage() {
  const navigate = useNavigate();
  const { isFinancial } = useRole();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('this_year');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Data state
  const [companyMetrics, setCompanyMetrics] = useState(null);
  const [projectFinancials, setProjectFinancials] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [cashFlowData, setCashFlowData] = useState([]);

  // Fetch all financial data
  const fetchFinancialData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const [metricsRes, projectsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/financials/metrics`, { params: { date_range: dateRange } }),
        axios.get(`${API}/financials/projects`, { params: { date_range: dateRange, status: statusFilter } }),
        axios.get(`${API}/financials/transactions`, { params: { limit: 10 } }),
      ]);
      
      setCompanyMetrics(metricsRes.data);
      setProjectFinancials(projectsRes.data.projects || []);
      setRecentTransactions(transactionsRes.data.transactions || []);
      setCashFlowData(metricsRes.data.cash_flow || []);
    } catch (error) {
      console.error('Error fetching financial data:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Financial access page protection
    if (!isFinancial) {
      toast.error('Access denied. Financial privileges required.');
      navigate('/dashboard');
      return;
    }
    fetchFinancialData();
  }, [dateRange, statusFilter, navigate, isFinancial]);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    if (!companyMetrics) return null;
    
    return {
      totalRevenue: companyMetrics.total_revenue || 0,
      totalExpenses: companyMetrics.total_expenses || 0,
      netProfit: (companyMetrics.total_revenue || 0) - (companyMetrics.total_expenses || 0),
      profitMargin: companyMetrics.total_revenue 
        ? (((companyMetrics.total_revenue - companyMetrics.total_expenses) / companyMetrics.total_revenue) * 100)
        : 0,
      activeProjects: companyMetrics.active_projects || 0,
      pendingInvoices: companyMetrics.pending_invoices || 0,
      overdueAmount: companyMetrics.overdue_amount || 0,
    };
  }, [companyMetrics]);

  // Projects needing attention
  const projectsNeedingAttention = useMemo(() => {
    return projectFinancials.filter(p => {
      const budgetUsed = p.actual_cost && p.estimated_budget 
        ? (p.actual_cost / p.estimated_budget) * 100 
        : 0;
      return budgetUsed > 80 || p.status === 'on_hold';
    });
  }, [projectFinancials]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-[#70170F] mx-auto mb-4" />
          <p className="text-muted-foreground">Loading financial data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="financials-page">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-4">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-3 text-muted-foreground">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="hover:underline hover:text-foreground"
            >
              My Desk
            </button>
            <span>/</span>
            <span className="text-foreground">Back of the House</span>
          </nav>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                My Desk
              </Button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-2 relative">
                <HelpIcon elementId="financials-page" position="inline" />
                <div>
                  <h1 className="font-heading font-light text-2xl tracking-tight text-foreground">
                    Back of the House
                  </h1>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Financial Overview • <span className="text-[#BC5D41] font-heading">KEY-MADE</span>
                  </p>
                </div>
                <BlueprintIndicator sectionId="financials-overview" tooltip="Learn about Financials" />
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Blueprint Help */}
              
              {/* Date Range Filter */}
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-36" data-testid="date-range-select">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="this_quarter">This Quarter</SelectItem>
                  <SelectItem value="this_year">This Year</SelectItem>
                  <SelectItem value="all_time">All Time</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Refresh */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => fetchFinancialData(true)}
                      disabled={refreshing}
                      data-testid="refresh-btn"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Refresh data</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              {/* Export */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="export-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => toast.info('CSV export coming soon')}>
                    <FileText className="w-4 h-4 mr-2" />
                    Export to CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => toast.info('Xero integration coming soon')}>
                    <Building2 className="w-4 h-4 mr-2" />
                    Export to Xero
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Budget Tracker Link */}
              <Button 
                className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
                onClick={() => navigate('/financials/budgets')}
                data-testid="budget-tracker-btn"
              >
                <Target className="w-4 h-4 mr-2" />
                Budget Tracker
              </Button>
              
              {/* Invoices Link */}
              <Button 
                variant="outline"
                onClick={() => navigate('/financials/invoices')}
                data-testid="invoices-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Invoices
              </Button>
              
              {/* Change Orders Link */}
              <Button 
                variant="outline"
                onClick={() => navigate('/financials/change-orders')}
                data-testid="change-orders-btn"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Change Orders
              </Button>
              
              {/* RFPs & Procurement Link */}
              <Button 
                variant="outline"
                onClick={() => navigate('/financials/rfps')}
                data-testid="rfps-btn"
              >
                <Receipt className="w-4 h-4 mr-2" />
                RFPs & POs
              </Button>
              
              {/* Insurance Audit Link */}
              <Button 
                variant="outline"
                onClick={() => navigate('/financials/insurance-audit')}
                data-testid="insurance-audit-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                Insurance Audit
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 md:px-12 py-8">
        {/* Top Metrics Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <MetricCard
            title="Total Revenue"
            value={formatCurrency(summaryMetrics?.totalRevenue || 0)}
            icon={DollarSign}
            trend={companyMetrics?.revenue_trend}
            trendLabel="vs last period"
            helpId="financials-revenue"
          />
          <MetricCard
            title="Total Expenses"
            value={formatCurrency(summaryMetrics?.totalExpenses || 0)}
            icon={CreditCard}
            trend={companyMetrics?.expense_trend}
            trendLabel="vs last period"
            color="text-red-600"
            helpId="financials-expenses"
          />
          <MetricCard
            title="Net Profit"
            value={formatCurrency(summaryMetrics?.netProfit || 0)}
            subtitle={`${summaryMetrics?.profitMargin?.toFixed(1) || 0}% margin`}
            icon={TrendingUp}
            color={summaryMetrics?.netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}
            helpId="financials-profit"
          />
          <MetricCard
            title="Active Projects"
            value={summaryMetrics?.activeProjects || 0}
            subtitle={`${projectsNeedingAttention.length} need attention`}
            icon={Layers}
            helpId="report-active-projects"
          />
        </div>

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-muted/50 p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-white">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="projects" className="data-[state=active]:bg-white">
              <Layers className="w-4 h-4 mr-2" />
              Projects
            </TabsTrigger>
            <TabsTrigger value="expenses" className="data-[state=active]:bg-white">
              <Receipt className="w-4 h-4 mr-2" />
              Expenses
            </TabsTrigger>
            <TabsTrigger value="draws" className="data-[state=active]:bg-white">
              <FileText className="w-4 h-4 mr-2" />
              Draw Requests
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Cash Flow & Profit Widgets - Top Row */}
            <div className="grid lg:grid-cols-2 gap-6">
              <CashFlowWidget />
              <ProfitByProjectWidget limit={5} />
            </div>
            
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Cash Flow Chart - Historical View */}
              <Card className="lg:col-span-2">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div>
                        <CardTitle className="text-base font-medium">Historical Cash Flow</CardTitle>
                        <CardDescription>Income vs Expenses over time</CardDescription>
                      </div>
                      <BlueprintIndicator sectionId="cash-flow" tooltip="Understanding Cash Flow" size="sm" />
                    </div>
                    <Badge variant="outline" className="font-normal">
                      {dateRange.replace('_', ' ')}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {cashFlowData.length > 0 ? (
                    <CashFlowChart data={cashFlowData} />
                  ) : (
                    <div className="h-48 flex items-center justify-center text-muted-foreground">
                      <div className="text-center">
                        <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No cash flow data yet</p>
                        <p className="text-xs">Start tracking expenses to see trends</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base font-medium">Quick Stats</CardTitle>
                    <BlueprintIndicator sectionId="quick-stats" tooltip="About Quick Stats" size="sm" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Clock className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Pending Invoices</p>
                        <p className="text-xs text-muted-foreground">Awaiting payment</p>
                      </div>
                    </div>
                    <span className="font-semibold text-amber-600">
                      {formatCurrency(summaryMetrics?.pendingInvoices || 0)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Overdue</p>
                        <p className="text-xs text-muted-foreground">Past due date</p>
                      </div>
                    </div>
                    <span className="font-semibold text-red-600">
                      {formatCurrency(summaryMetrics?.overdueAmount || 0)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Paid This Period</p>
                        <p className="text-xs text-muted-foreground">Completed payments</p>
                      </div>
                    </div>
                    <span className="font-semibold text-emerald-600">
                      {formatCurrency(companyMetrics?.paid_this_period || 0)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Transactions & Projects Needing Attention */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent Transactions */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-medium">Recent Transactions</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab('expenses')}>
                      View All
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {recentTransactions.length > 0 ? (
                    <div className="divide-y divide-border">
                      {recentTransactions.slice(0, 5).map((tx, idx) => (
                        <TransactionRow key={tx.id || idx} transaction={tx} />
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-muted-foreground">
                      <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No transactions yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Projects Needing Attention */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base font-medium">Needs Attention</CardTitle>
                      <CardDescription>Projects over 80% budget or on hold</CardDescription>
                    </div>
                    <Badge variant="secondary">{projectsNeedingAttention.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {projectsNeedingAttention.length > 0 ? (
                    <div>
                      {projectsNeedingAttention.slice(0, 4).map((project) => (
                        <ProjectFinancialRow
                          key={project.id}
                          project={project}
                          onClick={() => navigate(`/projects/${project.id}/hub?tab=financials`)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-muted-foreground">
                      <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500 opacity-50" />
                      <p className="text-sm">All projects on track!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-6">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-medium">Project Financials</CardTitle>
                    <CardDescription>Budget vs Actual for all projects</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-32">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="on_hold">On Hold</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Header Row */}
                <div className="flex items-center gap-4 px-4 py-2 bg-muted/30 text-xs text-muted-foreground border-b">
                  <div className="flex-1">Project</div>
                  <div className="w-32 hidden md:block text-center">Budget Used</div>
                  <div className="w-24 hidden sm:block text-right">Estimated</div>
                  <div className="w-24 text-right">Actual</div>
                  <div className="w-24 hidden lg:block text-right">Variance</div>
                  <div className="w-20">Status</div>
                  <div className="w-4"></div>
                </div>
                
                {projectFinancials.length > 0 ? (
                  <div>
                    {projectFinancials.map((project) => (
                      <ProjectFinancialRow
                        key={project.id}
                        project={project}
                        onClick={() => navigate(`/projects/${project.id}/hub?tab=financials`)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center text-muted-foreground">
                    <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">No projects found</p>
                    <p className="text-sm">Create a project to start tracking financials</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Expenses Tab */}
          <TabsContent value="expenses" className="space-y-6">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-medium">Expense Log</CardTitle>
                    <CardDescription>All recorded expenses and income</CardDescription>
                  </div>
                  <Button 
                    className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
                    onClick={() => toast.info('Expense entry coming in Phase 2')}
                    data-testid="add-expense-btn"
                  >
                    <Receipt className="w-4 h-4 mr-2" />
                    Add Expense
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {recentTransactions.length > 0 ? (
                  <div className="divide-y divide-border">
                    {recentTransactions.map((tx, idx) => (
                      <TransactionRow key={tx.id || idx} transaction={tx} />
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center text-muted-foreground">
                    <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">No expenses recorded</p>
                    <p className="text-sm">Click "Add Expense" to start tracking</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Draw Requests Tab */}
          <TabsContent value="draws" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>All Draw Requests</CardTitle>
                    <CardDescription>Draw requests across all projects</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <DrawRequestsBackOfHouse />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
